import { useState } from 'react'
import { StatusPicker } from './HydrantEditModal'

export interface HydrantFormData {
  name: string
  address: string
  is_flushing_point: boolean
  notes: string
  inspection_status: string
}

interface HydrantCreationModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: HydrantFormData) => void
  coordinates: [number, number] | null // [lng, lat]
}

export default function HydrantCreationModal({ isOpen, onClose, onSave, coordinates }: HydrantCreationModalProps) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [isFlushingPoint, setIsFlushingPoint] = useState(false)
  const [notes, setNotes] = useState('')
  const [inspectionStatus, setInspectionStatus] = useState('unchecked')

  if (!isOpen) return null

  const reset = () => {
    setName('')
    setAddress('')
    setIsFlushingPoint(false)
    setNotes('')
    setInspectionStatus('unchecked')
  }

  const handleSave = () => {
    onSave({ name: name.trim(), address: address.trim(), is_flushing_point: isFlushingPoint, notes: notes.trim(), inspection_status: inspectionStatus })
    reset()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[9999] sm:items-center sm:p-4" onClick={handleClose}>
      <div className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">🚒 Nowy hydrant</h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {coordinates && (
            <div className="bg-gray-50 rounded-lg px-4 py-2 text-sm text-gray-600">
              📍 Lokalizacja: {coordinates[1].toFixed(6)}, {coordinates[0].toFixed(6)}
            </div>
          )}

          {/* Name / ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa / ID hydrantu
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Zostaw puste, aby nadać automatycznie (np. H-0001)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500">Puste pole = system nada identyfikator automatycznie.</p>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lokalizacja administracyjna (adres)
            </label>
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="np. ul. Główna 12, Miejscowość"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Flushing point */}
          <label className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isFlushingPoint}
              onChange={e => setIsFlushingPoint(e.target.checked)}
              className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-800">💦 Punkt płukania sieci</span>
          </label>

          {/* Inspection status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Wynik kontroli ciśnienia i wydajności
            </label>
            <StatusPicker value={inspectionStatus} onChange={setInspectionStatus} />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Uwagi (opcjonalnie)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Dodatkowe informacje o hydrancie..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={handleClose}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Utwórz hydrant
          </button>
        </div>
      </div>
    </div>
  )
}
